import { defineStore } from 'pinia';
import TaskAPI from 'dashboard/api/tasks';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useTasksStore = defineStore('tasks', {
  state: () => ({
    records: [],
    selectedTask: null,
    myTasks: [],
    stats: null,
    searchResults: [],
    uiFlags: {
      fetchingList: false,
      fetchingItem: false,
      creating: false,
      updating: false,
      deleting: false,
      completing: false,
      fetchingMyTasks: false,
      fetchingStats: false,
      searching: false,
    },
    meta: {},
    currentFilters: {
      page: 1,
      sort: 'due_date_asc',
      status: '',
      assignee: '',
      search: '',
    },
  }),

  getters: {
    getTasksList: state => camelcaseKeys(state.records, { deep: true }),
    getSelectedTask: state => {
      return state.selectedTask
        ? camelcaseKeys(state.selectedTask, { deep: true })
        : null;
    },
    getUIFlags: state => state.uiFlags,
    getMeta: state => state.meta,
    getMyTasks: state => camelcaseKeys(state.myTasks, { deep: true }),
    getStats: state => state.stats,
    getSearchResults: state =>
      camelcaseKeys(state.searchResults, { deep: true }),
    getCurrentFilters: state => state.currentFilters,
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    setMeta(meta) {
      this.meta = meta;
    },

    setFilters(filters) {
      this.currentFilters = { ...this.currentFilters, ...filters };
    },

    async get({ page, status, assignee, sort, search } = {}) {
      const params = {
        page: page ?? this.currentFilters.page,
        status: status ?? this.currentFilters.status,
        assignee: assignee ?? this.currentFilters.assignee,
        sort: sort ?? this.currentFilters.sort,
        search: search ?? this.currentFilters.search,
      };

      this.setFilters(params);
      this.setUIFlag({ fetchingList: true });

      try {
        const {
          data: { payload, meta },
        } = await TaskAPI.get(params);
        this.records = payload;
        this.setMeta(meta);
      } catch (error) {
        throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async search({ q = '', limit = 10 }) {
      if (!q || q.length < 2) {
        this.searchResults = [];
        return this.searchResults;
      }

      this.setUIFlag({ searching: true });
      try {
        const { data } = await TaskAPI.search({ q, limit });
        this.searchResults = data.payload || data || [];
        return this.searchResults;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ searching: false });
      }
    },

    async fetchMyTasks() {
      this.setUIFlag({ fetchingMyTasks: true });
      try {
        const { data } = await TaskAPI.myTasks();
        this.myTasks = data;
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ fetchingMyTasks: false });
      }
    },

    async fetchStats() {
      this.setUIFlag({ fetchingStats: true });
      try {
        const { data } = await TaskAPI.stats();
        this.stats = data;
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ fetchingStats: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const { data } = await TaskAPI.show(id);
        this.selectedTask = data.payload || data;
        return data.payload || data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.uiFlags.fetchingItem = false;
      }
    },

    async create(taskData) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await TaskAPI.create(taskData);
        const task = data.payload || data;
        this.records.unshift(task);
        return task;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(id, taskData) {
      this.setUIFlag({ updating: true });
      try {
        const { data } = await TaskAPI.update(id, taskData);
        const task = data.payload || data;
        const index = this.records.findIndex(t => t.id === id);
        if (index !== -1) {
          this.records[index] = task;
        }
        if (this.selectedTask?.id === id) {
          this.selectedTask = task;
        }
        return task;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deleting: true });
      try {
        await TaskAPI.delete(id);
        this.records = this.records.filter(t => t.id !== id);
        if (this.selectedTask?.id === id) {
          this.selectedTask = null;
        }
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    async complete(id) {
      this.setUIFlag({ completing: true });
      try {
        const { data } = await TaskAPI.complete(id);
        const task = data.payload || data;
        const index = this.records.findIndex(t => t.id === id);
        if (index !== -1) {
          this.records[index] = task;
        }
        if (this.selectedTask?.id === id) {
          this.selectedTask = task;
        }
        return task;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ completing: false });
      }
    },

    clearSelectedTask() {
      this.selectedTask = null;
    },

    clearSearch() {
      this.searchResults = [];
    },
  },
});
